/**
 * ParcelMap — renders the subject cadastral parcel and its immediate
 * neighbors as a pure SVG illustration.
 *
 * No tile layer (keeps the bundle light), no external map library —
 * everything is derived from the GeoJSON geometry we already store on the
 * fiche plus a best-effort fetch to pull neighboring polygons for visual
 * context.
 *
 * Source : IGN Géoplateforme WFS 2.0.0
 * (`BDPARCELLAIRE-VECTEUR_WLD_BDD_WGS84G:parcelle`). On avait d'abord pointé
 * la couche `CADASTRALPARCELS.PARCELLAIRE_EXPRESS:parcelle` qu'IGN a retirée
 * silencieusement le 2026-04-24 — chaque requête retournait alors un 500 et
 * le fetch voisins restait vide indéfiniment. Cette couche-ci est celle
 * qu'on utilise aussi côté Cloud Functions (cf. `functions/src/cadastreApi.ts`).
 *
 * Différences de schéma vs. l'ancienne couche :
 *   - pas de `idu` (ID 14 chars DGFiP) → on le reconstruit depuis
 *     code_dep+code_com+com_abs+section+numero, même règle que le backend.
 *   - pas de `contenance` — pas utilisé ici, on affiche juste les voisins.
 *   - bbox WFS doit se déclarer en URN EPSG:4326 (lat,lng) — CRS84 lng,lat
 *     qui marchait avant renvoie zéro feature sur cette couche.
 *
 * Support MULTI-parcels : quand l'utilisateur a pické plusieurs parcelles
 * dans le modal (`parcel.id = "MULTI:id1+id2+…"`), le filtre voisins doit
 * exclure chacune des composantes, pas juste la string composite. Sinon les
 * sous-parcelles du sujet apparaîtraient à la fois en surbrillance et
 * comme voisins gris — doublon visuel.
 *
 * Projection: equirectangular with a cos(lat) correction on the X axis so
 * the aspect ratio is preserved on French latitudes. This is accurate
 * enough at the scale of a single parcel (a few dozen meters).
 */

import { useEffect, useMemo, useState } from "react";
import { Pill } from "./ui";
import { Ic } from "./icons";
import type { CadastreParcel } from "../lib/types";
import { parcelLabel } from "../lib/parcelMerge";

type GeoJSONPolygon = {
  type: "Polygon";
  coordinates: number[][][]; // [ring][vertex][lng,lat]
};
type GeoJSONMultiPolygon = {
  type: "MultiPolygon";
  coordinates: number[][][][]; // [poly][ring][vertex][lng,lat]
};
type AnyGeom = GeoJSONPolygon | GeoJSONMultiPolygon | null | undefined;

type NeighborFeature = {
  properties: {
    // `BDPARCELLAIRE-VECTEUR` feature schema — no `idu`, the stable ID has
    // to be reconstructed from code_dep+code_com+com_abs+section+numero
    // (same convention as `functions/src/cadastreApi.ts#featureToParcel`).
    section?: string;
    numero?: string;
    code_dep?: string;
    code_com?: string;
    com_abs?: string;
  };
  geometry: AnyGeom;
};

/** Rebuild the 14-char cadastre ID from a WFS feature. */
function featureId(f: NeighborFeature): string {
  const p = f.properties;
  const section = (p.section ?? "").toUpperCase().padStart(2, "0");
  const numero = (p.numero ?? "").padStart(4, "0");
  const prefixe = p.com_abs ?? "000";
  return `${p.code_dep ?? ""}${p.code_com ?? ""}${prefixe}${section}${numero}`;
}

/**
 * Return every cadastre ID the subject parcel is "composed of". For a
 * normal single parcel this is a singleton; for a `MULTI:` composite
 * built by the picker this is the list of component IDs.
 */
function subjectIds(parcelId: string): string[] {
  if (parcelId.startsWith("MULTI:")) {
    return parcelId.slice("MULTI:".length).split("+").filter(Boolean);
  }
  return [parcelId];
}

// Viewport target — stays constant; the SVG scales to its container via
// preserveAspectRatio="xMidYMid meet". Content is projected to fit.
const VIEW_W = 600;
const VIEW_H = 400;
const PADDING = 24;

// Neighbor search radius (in meters) around the subject parcel's bbox.
// Large enough to show adjacent plots on both sides, not so large that
// the subject becomes tiny in the frame.
const NEIGHBOR_BUFFER_M = 40;

export function ParcelMap({
  parcel,
  className = "",
}: {
  parcel: CadastreParcel;
  className?: string;
}) {
  const subjectGeom = parcel.geometry as AnyGeom;
  const [neighbors, setNeighbors] = useState<NeighborFeature[]>([]);

  // Fetch neighbors best-effort. On failure (network error, IGN downtime,
  // CORS change) we silently fall back to "subject only" — the subject still
  // renders from the geometry cached in the fiche.
  useEffect(() => {
    let cancelled = false;
    const bb = bboxOfGeom(subjectGeom);
    if (!bb) return;
    const padded = expandBbox(bb, NEIGHBOR_BUFFER_M);
    // BDPARCELLAIRE-VECTEUR wants lat,lng axis order (URN EPSG:4326). CRS84
    // (which the retired PARCELLAIRE_EXPRESS layer accepted in lng,lat) now
    // returns zero feature, learned the hard way during the cadastreApi
    // migration. Kept as URN here for consistency with functions/src/cadastreApi.ts.
    const bboxParam = `${padded.minLat},${padded.minLng},${padded.maxLat},${padded.maxLng},urn:ogc:def:crs:EPSG::4326`;
    const params = new URLSearchParams({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      typeNames: "BDPARCELLAIRE-VECTEUR_WLD_BDD_WGS84G:parcelle",
      outputFormat: "application/json",
      count: "50",
      bbox: bboxParam,
    });
    const url = `https://data.geopf.fr/wfs/ows?${params.toString()}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled || !body?.features) return;
        // Exclude the subject parcel(s) from the neighbors list. For a
        // MULTI-composite this means excluding each component individually
        // — otherwise the sub-parcels would render twice (once as subject
        // highlight, once as a gray neighbor).
        const subjectSet = new Set(subjectIds(parcel.id));
        setNeighbors(
          (body.features as NeighborFeature[]).filter(
            (f) => !subjectSet.has(featureId(f)),
          ),
        );
      })
      .catch(() => {
        // Non-fatal; keep neighbors empty.
      });
    return () => {
      cancelled = true;
    };
  }, [parcel.id, subjectGeom]);

  const projection = useMemo(() => {
    // Pick the tightest bbox that still covers subject + any fetched neighbors
    // whose bbox overlaps the buffered subject bbox.
    const subjectBb = bboxOfGeom(subjectGeom);
    if (!subjectBb) return null;
    const targetBb = expandBbox(subjectBb, NEIGHBOR_BUFFER_M * 0.75);

    // Fit targetBb into the viewport preserving aspect ratio.
    const centerLat = (targetBb.minLat + targetBb.maxLat) / 2;
    const lngScale = Math.cos((centerLat * Math.PI) / 180);
    const dxDeg = (targetBb.maxLng - targetBb.minLng) * lngScale;
    const dyDeg = targetBb.maxLat - targetBb.minLat;
    const boxW = VIEW_W - 2 * PADDING;
    const boxH = VIEW_H - 2 * PADDING;
    const scale = Math.min(boxW / dxDeg, boxH / dyDeg);
    const offsetX = (VIEW_W - dxDeg * scale) / 2;
    const offsetY = (VIEW_H - dyDeg * scale) / 2;

    const project = (lng: number, lat: number) => [
      offsetX + (lng - targetBb.minLng) * lngScale * scale,
      offsetY + (targetBb.maxLat - lat) * scale, // flip Y: higher lat = lower y
    ];

    // Scale bar: pick a nice round meter length that fits in ~1/6 of width.
    const metersPerUnit = 1 / (scale * 111_320); // units = degrees of latitude
    const targetMeters = VIEW_W / 6 / (scale * 111_320);
    const barMeters = niceRound(targetMeters);
    const barPixels = barMeters * scale * 111_320;

    return { project, barMeters, barPixels };
  }, [subjectGeom]);

  if (!projection || !subjectGeom) {
    return (
      <div
        className={`relative rounded-xl2 overflow-hidden bg-ink-50 flex items-center justify-center ${className}`}
      >
        <div className="text-[11.5px] text-ink-500">
          Pas de géométrie pour cette parcelle
        </div>
      </div>
    );
  }

  const subjectPath = geomToSvgPath(subjectGeom, projection.project);
  const centroid = centroidOfGeom(subjectGeom, projection.project);

  return (
    <div
      className={`relative rounded-xl2 overflow-hidden map-grid hairline ${className}`}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
      >
        {/* Neighboring parcels — thin stroke, subtle fill. */}
        <g
          stroke="rgba(20,22,26,0.35)"
          strokeWidth="0.7"
          fill="rgba(255,255,255,0.55)"
        >
          {neighbors.map((f, i) => {
            const d = geomToSvgPath(f.geometry, projection.project);
            if (!d) return null;
            return <path key={featureId(f) || i} d={d} />;
          })}
        </g>

        {/* Subject parcel — lime highlight, dashed outline to match the
            existing Cadastre mockup aesthetic. */}
        <path
          d={subjectPath}
          fill="rgba(217,234,44,0.55)"
          stroke="#14161A"
          strokeWidth="1.4"
          className="dashed-parcel"
        />

        {/* Surface chip in the centroid — only render if the chip fits
            inside the projected parcel bbox, otherwise skip (tiny slivers). */}
        {centroid && (
          <g>
            <rect
              x={centroid.x - 28}
              y={centroid.y - 10}
              width="56"
              height="20"
              rx="3"
              fill="rgba(20,22,26,0.85)"
            />
            <text
              x={centroid.x}
              y={centroid.y + 4}
              textAnchor="middle"
              fontSize="11"
              fill="#F5FCAA"
              fontFamily="JetBrains Mono"
            >
              {parcel.surface_m2.toLocaleString("fr-FR")} m²
            </text>
          </g>
        )}

        {/* Scale bar. */}
        <g transform={`translate(16 ${VIEW_H - 16})`}>
          <rect
            x="0"
            y="0"
            width={projection.barPixels}
            height="3"
            fill="#14161A"
          />
          <text
            x={projection.barPixels + 6}
            y="5"
            fontSize="10"
            fill="#14161A"
            fontFamily="JetBrains Mono"
          >
            {projection.barMeters} m
          </text>
        </g>
      </svg>

      <div className="absolute top-3 left-3">
        <Pill tone="soft" icon={<Ic.Pin size={12} />}>
          Parcelle {parcelLabel(parcel)}
        </Pill>
      </div>
    </div>
  );
}

// ─── Geometry helpers ───────────────────────────────────────────────────

type Bbox = { minLng: number; minLat: number; maxLng: number; maxLat: number };

function bboxOfGeom(geom: AnyGeom): Bbox | null {
  if (!geom) return null;
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  const visit = (ring: number[][]) => {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  };
  if (geom.type === "Polygon") geom.coordinates.forEach(visit);
  else if (geom.type === "MultiPolygon")
    geom.coordinates.forEach((p) => p.forEach(visit));
  if (!Number.isFinite(minLng)) return null;
  return { minLng, minLat, maxLng, maxLat };
}

function expandBbox(bb: Bbox, meters: number): Bbox {
  const dLat = meters / 111_320;
  const centerLat = (bb.minLat + bb.maxLat) / 2;
  const dLng = meters / (111_320 * Math.cos((centerLat * Math.PI) / 180));
  return {
    minLng: bb.minLng - dLng,
    maxLng: bb.maxLng + dLng,
    minLat: bb.minLat - dLat,
    maxLat: bb.maxLat + dLat,
  };
}

function geomToSvgPath(
  geom: AnyGeom,
  project: (lng: number, lat: number) => number[],
): string {
  if (!geom) return "";
  const ringPath = (ring: number[][]) => {
    if (!ring.length) return "";
    const [x0, y0] = project(ring[0][0], ring[0][1]);
    let d = `M${x0.toFixed(2)} ${y0.toFixed(2)}`;
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = project(ring[i][0], ring[i][1]);
      d += ` L${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    return d + " Z";
  };
  if (geom.type === "Polygon") {
    return geom.coordinates.map(ringPath).join(" ");
  }
  if (geom.type === "MultiPolygon") {
    return geom.coordinates
      .map((rings) => rings.map(ringPath).join(" "))
      .join(" ");
  }
  return "";
}

// Approximate centroid via average of outer-ring vertices of the largest
// polygon. Good enough for label placement on simple parcel shapes.
function centroidOfGeom(
  geom: AnyGeom,
  project: (lng: number, lat: number) => number[],
): { x: number; y: number } | null {
  if (!geom) return null;
  let outer: number[][] | null = null;
  if (geom.type === "Polygon") outer = geom.coordinates[0] ?? null;
  else if (geom.type === "MultiPolygon") {
    // Pick the polygon with the largest outer ring (by vertex count as a
    // proxy for size — fine at this scale).
    let best: number[][] | null = null;
    let bestLen = 0;
    for (const rings of geom.coordinates) {
      const ring = rings[0];
      if (ring && ring.length > bestLen) {
        best = ring;
        bestLen = ring.length;
      }
    }
    outer = best;
  }
  if (!outer || !outer.length) return null;
  let sx = 0,
    sy = 0,
    n = 0;
  for (const [lng, lat] of outer) {
    const [x, y] = project(lng, lat);
    sx += x;
    sy += y;
    n++;
  }
  return { x: sx / n, y: sy / n };
}

// Round to a "nice" scale-bar length: 5, 10, 20, 25, 50, 100, ...
function niceRound(m: number): number {
  if (m <= 0) return 10;
  const exp = Math.floor(Math.log10(m));
  const base = Math.pow(10, exp);
  const frac = m / base;
  let nice: number;
  if (frac < 1.5) nice = 1;
  else if (frac < 3) nice = 2;
  else if (frac < 7) nice = 5;
  else nice = 10;
  return Math.max(1, Math.round(nice * base));
}
